import React, { useContext, useReducer, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useLocation, useNavigate } from '@reach/router';
import queryString from 'query-string';
import { Box } from 'wt-ui';

import Header from '../Header';
import Table from '../Table';

import { actions, reducer } from '../../services/reducer';
import VariationContext from '../../services/VariationContext';
import variations from '../../services/variations';

import API from '../../services/API';

const getProductTickerFromPage = () => {
  try {
    return document
      .getElementsByClassName('ticker-code')[0]
      .innerHTML.trim()
      .split('/')[0];
  } catch (e) {
    // eslint-disable-next-line
    console.log('Could not get ticker-code.');
  }
  return window.performanceApp.ticker;
};
const getProductNameFromPage = () => {
  try {
    return document.getElementsByClassName('fund-name')[0].innerHTML.trim();
  } catch (e) {
    // eslint-disable-next-line
    console.log('Could not get fund-name.');
  }
  return null;
};
//NOSONAR
const getInitialState = (variation, location, fundList) => {
  const now = new Date();
  if (variation === variations.embedded) {
    // get product ticker from page and initialize state
    const ticker = getProductTickerFromPage();
    let fund = fundList.find((x) => x.exchangeCode === ticker) || null;
    if (!fund) {
      const name = getProductNameFromPage();
      fund = fundList.find((x) => x.displayName === name) || {};
    }
    return {
      loading: true,
      data: [],
      fundData: fund,
      filters: {
        ticker: fund.bloombergTickerPrimary || fund.bloombergTicker,
        //NOSONAR
        startDate: new Date(now - 1000 * 60 * 60 * 24 * 14).toISOString(),
        endDate: now.toISOString(),
      },
    };
  }
  if (variation === variations.standalone) {
    // parse query string to initialize state
    const qs = queryString.parse(location.search);

    return {
      loading: false,
      data: [],
      fundData:
        qs.ticker && fundList.find((x) => (x.bloombergTickerPrimary || x.bloombergTicker) === qs.ticker)
          ? fundList.find((x) => (x.bloombergTickerPrimary || x.bloombergTicker) === qs.ticker)
          : null,
      fundList: [],
      filters: {
        ticker: qs.ticker,
        //NOSONAR
        startDate: qs.startDate || new Date(now - 1000 * 60 * 60 * 24 * 14).toISOString(),
        endDate: qs.endDate || now.toISOString(),
      },
    };
  }
  throw new Error('Invalid app variation.');
};

const Main = ({ fundList }) => {
  const variation = useContext(VariationContext);
  const location = useLocation();
  const navigate = useNavigate();
  const [state, dispatch] = useReducer(reducer, getInitialState(variation, location, fundList));

  useEffect(() => {
    if (!state.filters.ticker) {
      return;
    }
    const fetchData = async () => {
      const result = await API.getPerformance(state.filters);
      dispatch({ type: actions.setData, value: result });
      // update query string;
      // remove filters from state and manage them ONLY using qs
      if (variation === variations.standalone) {
        navigate(`${location.pathname}?${queryString.stringify(state.filters)}`);
      }
    };

    dispatch({ type: actions.setLoading, value: true });
    fetchData();
  }, [state.filters, navigate, variation, location.pathname]);

  if (!fundList || fundList.length <= 0) {
    return <>Loading</>;
  }

  if ((state.data && state.data.error) || (variation === variations.embedded && !state.filters.ticker)) {
    return <></>;
  }

  return (
    <Box background="transparent" gap="medium">
      <Header dispatch={dispatch} filters={state.filters} fundList={fundList} />
      <Table data={state.data} filters={state.filters} loading={state.loading} fundData={state.fundData} />
    </Box>
  );
};

Main.propTypes = {
  fundList: PropTypes.arrayOf(PropTypes.object).isRequired,
};

const MainWrapper = () => {
  // app requires fund list to operate
  const [fundList, setFundList] = useState(null);
  const fetchFundList = async () => {
    const result = await API.getProductList();
    setFundList(result);
  };

  useEffect(() => {
    fetchFundList();
  }, []);

  if (!fundList) {
    return <></>;
  }
  return <Main fundList={fundList} />;
};

export default MainWrapper;
